// Atlas bake (IMPLEMENTATION §2.9 "Bake size", M0 decision 2): every catalog frame is drawn by the M8
// generators at cellPx = bucket · dprEff device pixels per cell, trimmed to its alpha bounds, packed with
// maxrects-packer into power-of-two pages ≤ `pageMax`² (`padding` px apart, every frame's edge pixels
// extruded by `extrude` px against mipmap / LINEAR bleeding) and written into the page canvases. Frames
// keep their untrimmed source size and trim offset (TexturePacker JSON-hash semantics); the renderer
// registers them with Phaser's frame trim (render/phaser/frames.ts), so a trimmed frame is drawn exactly
// where the full cell frame would be. One bake at boot; pages are sampled with LINEAR_MIPMAP_LINEAR and
// never rebaked on expansion. The same code bakes in the browser (DOM canvas) and in Node (@napi-rs/canvas,
// `npm run bake`, tests/art). Canvas memory (§2.9, iOS): ONE scratch canvas (the largest frame) is reused
// for every frame and freed at the end; trimmed pixels wait in plain typed arrays until the pages exist.
import { MaxRectsPacker } from 'maxrects-packer';
import type { Version } from '../sim/gen/ids.ts';
import { artEnv, buildCatalog } from './catalog.ts';
import { type CanvasLike, type Ctx2D, contextOf, createCanvas, freeCanvas } from './ctx.ts';
import { drawFrame, frameSize } from './generators.ts';
import { Sha256 } from './sha256.ts';
import type { ArtDefs, FrameSpec } from './types.ts';

export type { CanvasLike, Ctx2D } from './ctx.ts';

export interface PackedFrame {
  id: string;
  page: number;
  /** Trimmed frame rect on the page (inside the extrude border). */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Untrimmed source size (the frame spec at cellPx) and the trim offset inside it. */
  sw: number;
  sh: number;
  tx: number;
  ty: number;
  pivot: { x: number; y: number };
}

export interface AtlasLayout {
  pages: { w: number; h: number }[];
  /** In catalog order. */
  frames: PackedFrame[];
}

export interface AtlasOptions {
  /** Pixels per cell (bucket · dprEff). */
  cellPx: number;
  pageMax: number;
  padding: number;
  /** Edge pixels copied outward around every frame (0 = none). */
  extrude: number;
}

/** A trimmed rect inside a frame's source box. */
export interface Trim {
  x: number;
  y: number;
  w: number;
  h: number;
}

/** Draws frame `f` at `px` device pixels per cell, its source box's top-left at the context origin. */
export type FrameDrawer = (g: Ctx2D, f: FrameSpec, px: number) => void;

/** Frame source size in pixels at `cellPx` (art/generators.ts frameSize). */
export function framePx(f: FrameSpec, cellPx: number): { w: number; h: number } {
  const [w, h] = frameSize(f, cellPx);
  return { w, h };
}

/**
 * Packs the catalog into POT pages (deterministic: same frames + options + trims → same layout). Without
 * `trims` every frame packs at its full source size (the worst case). Throws when a frame is larger than a
 * page.
 */
export function packAtlas(
  frames: readonly FrameSpec[],
  o: AtlasOptions,
  trims?: readonly Trim[],
): AtlasLayout {
  const e = o.extrude;
  const packer = new MaxRectsPacker(o.pageMax, o.pageMax, o.padding, {
    smart: true,
    pot: true,
    square: false,
    allowRotation: false,
    border: 0,
  });
  const src = frames.map((f, i) => {
    const { w: sw, h: sh } = framePx(f, o.cellPx);
    const t = trims?.[i] ?? { x: 0, y: 0, w: sw, h: sh };
    if (t.w + 2 * e > o.pageMax || t.h + 2 * e > o.pageMax)
      throw new RangeError(`frame ${f.key} (${t.w}×${t.h}) exceeds a page`);
    return { sw, sh, t };
  });
  packer.addArray(
    src.map((s, i) => {
      const r = { width: s.t.w + 2 * e, height: s.t.h + 2 * e, x: 0, y: 0, data: { i } };
      return r as unknown as Parameters<typeof packer.addArray>[0][number];
    }),
  );
  const pages: { w: number; h: number }[] = [];
  const out: (PackedFrame | undefined)[] = frames.map(() => undefined);
  packer.bins.forEach((bin, page) => {
    pages.push({ w: bin.width, h: bin.height });
    for (const r of bin.rects) {
      const i = (r.data as { i: number }).i;
      const f = frames[i] as FrameSpec;
      const s = src[i] as (typeof src)[number];
      out[i] = {
        id: f.key,
        page,
        x: r.x + e,
        y: r.y + e,
        w: s.t.w,
        h: s.t.h,
        sw: s.sw,
        sh: s.sh,
        tx: s.t.x,
        ty: s.t.y,
        pivot: f.pivot,
      };
    }
  });
  return { pages, frames: out.map((f, i) => f ?? missingFrame(frames[i])) };
}

function missingFrame(f: FrameSpec | undefined): never {
  throw new Error(`art: frame ${f?.key ?? '?'} was not packed`);
}

/** Alpha bounding box of RGBA data (w × h); empty → a 1×1 box at 0,0 with `empty`. */
export function alphaBounds(
  data: ArrayLike<number>,
  w: number,
  h: number,
): { x: number; y: number; w: number; h: number; empty: boolean } {
  let x0 = w;
  let y0 = h;
  let x1 = -1;
  let y1 = -1;
  for (let y = 0; y < h; y++) {
    const row = y * w * 4;
    for (let x = 0; x < w; x++) {
      if ((data[row + x * 4 + 3] ?? 0) !== 0) {
        if (x < x0) x0 = x;
        if (x > x1) x1 = x;
        if (y < y0) y0 = y;
        y1 = y;
      }
    }
  }
  if (x1 < 0) return { x: 0, y: 0, w: 1, h: 1, empty: true };
  return { x: x0, y: y0, w: x1 - x0 + 1, h: y1 - y0 + 1, empty: false };
}

/** The trimmed rect of `data` (source w wide) with `e` px of its edge pixels copied outward. */
function extruded(data: Uint8ClampedArray, w: number, t: Trim, e: number): Uint8ClampedArray {
  const W = t.w + 2 * e;
  const H = t.h + 2 * e;
  const out = new Uint8ClampedArray(W * H * 4);
  for (let y = 0; y < H; y++) {
    const sy = t.y + Math.min(t.h - 1, Math.max(0, y - e));
    const srow = (sy * w + t.x) * 4;
    const drow = y * W * 4;
    out.set(data.subarray(srow, srow + t.w * 4), drow + e * 4);
    for (let k = 0; k < e; k++) {
      out.set(data.subarray(srow, srow + 4), drow + k * 4);
      const last = srow + (t.w - 1) * 4;
      out.set(data.subarray(last, last + 4), drow + (e + t.w + k) * 4);
    }
  }
  return out;
}

export interface BakedAtlas<C extends CanvasLike> {
  layout: AtlasLayout;
  pages: C[];
  ms: number;
}

/**
 * Draws every frame (one reused scratch canvas), trims, packs and writes the page canvases. Throws on an
 * empty frame; the scratch canvas is freed on every path.
 */
export function bakeAtlas<C extends CanvasLike>(
  frames: readonly FrameSpec[],
  o: AtlasOptions,
  draw: FrameDrawer,
  makeCanvas: (w: number, h: number) => C = (w, h) => createCanvas(w, h) as C,
  now: () => number = () => 0,
): BakedAtlas<C> {
  const t0 = now();
  const e = o.extrude;
  let mw = 1;
  let mh = 1;
  for (const f of frames) {
    const s = framePx(f, o.cellPx);
    mw = Math.max(mw, s.w);
    mh = Math.max(mh, s.h);
  }
  const trims: Trim[] = [];
  const pixels: Uint8ClampedArray[] = [];
  const scratch = makeCanvas(mw, mh);
  try {
    const g = contextOf(scratch);
    for (const f of frames) {
      const { w, h } = framePx(f, o.cellPx);
      g.clearRect(0, 0, mw, mh);
      g.save();
      draw(g, f, o.cellPx);
      g.restore();
      const data = g.getImageData(0, 0, w, h).data;
      const b = alphaBounds(data, w, h);
      if (b.empty) throw new Error(`art: frame "${f.key}" is empty`);
      trims.push(b);
      pixels.push(extruded(data, w, b, e));
    }
  } finally {
    freeCanvas(scratch);
  }
  const layout = packAtlas(frames, o, trims);
  const pages = layout.pages.map((p) => makeCanvas(p.w, p.h));
  const ctxs = pages.map((c) => contextOf(c));
  layout.frames.forEach((r, i) => {
    const g = ctxs[r.page] as Ctx2D;
    const img = g.createImageData(r.w + 2 * e, r.h + 2 * e);
    img.data.set(pixels[i] as Uint8ClampedArray);
    g.putImageData(img, r.x - e, r.y - e);
  });
  return { layout, pages, ms: now() - t0 };
}

/** Share of the packed page area the frames (+ extrude + padding) use, over every page. */
export function atlasFill(
  layout: AtlasLayout,
  o: Pick<AtlasOptions, 'padding' | 'extrude'>,
): number {
  let used = 0;
  for (const f of layout.frames)
    used += (f.w + 2 * o.extrude + o.padding) * (f.h + 2 * o.extrude + o.padding);
  let total = 0;
  for (const p of layout.pages) total += p.w * p.h;
  return total > 0 ? used / total : 0;
}

// ---- Node bake / browser bench / tests: the whole catalog at a bucket × DPR --------------------------------

export interface Atlas {
  /** Page 0 (v0.1 fits one page). */
  canvas: CanvasLike;
  pages: CanvasLike[];
  layout: AtlasLayout;
  frames: Record<string, PackedFrame>;
  specs: FrameSpec[];
  /** Device pixels per cell. */
  px: number;
  /** Bucket in CSS px. */
  cellPx: number;
  dpr: number;
  bakeMs: number;
  opts: AtlasOptions;
  /** SHA-256 over every page's RGBA and the frame table (computed on first access). */
  readonly hash: string;
}

export interface BuildAtlasOpts {
  defs: ArtDefs;
  /** Bucket in CSS px (default ui.json render.bakeBucket = 96). */
  cellPx?: number;
  /** dprEff (default 2). */
  dpr?: number;
  version?: Version | 'all';
  /** Optional key filter (tests, previews). */
  only?: (spec: FrameSpec) => boolean;
  now?: () => number;
}

/** The runtime atlas options for a bucket × dprEff (ui.json render). */
export function atlasOptions(defs: ArtDefs, dprEff: number, bucketCss?: number): AtlasOptions {
  const r = defs.ui.render;
  return {
    cellPx: Math.ceil((bucketCss ?? r.bakeBucket) * dprEff),
    pageMax: r.atlasPageMax,
    padding: r.atlasPadding,
    extrude: r.atlasExtrude,
  };
}

/** Bakes the `atlas` frames of a version (the runtime set) with the generators. */
export function buildAtlas(o: BuildAtlasOpts): Atlas {
  const now =
    o.now ?? (() => (typeof performance !== 'undefined' ? performance.now() : Date.now()));
  const cellPx = o.cellPx ?? o.defs.ui.render.bakeBucket;
  const dpr = o.dpr ?? 2;
  const opts = atlasOptions(o.defs, dpr, cellPx);
  const env = artEnv(o.defs, dpr);
  const specs = buildCatalog(o.defs, { version: o.version ?? o.defs.sim.contentVersion })
    .filter((s) => s.atlas)
    .filter(o.only ?? (() => true));
  const baked = bakeAtlas(specs, opts, (g, f, px) => drawFrame(g, f, px, env), undefined, now);
  const frames: Record<string, PackedFrame> = {};
  for (const f of baked.layout.frames) frames[f.id] = f;
  const first = baked.pages[0];
  if (!first) throw new Error('art: empty atlas');
  let hash: string | null = null;
  return {
    canvas: first,
    pages: baked.pages,
    layout: baked.layout,
    frames,
    specs,
    px: opts.cellPx,
    cellPx,
    dpr,
    bakeMs: baked.ms,
    opts,
    get hash() {
      hash ??= atlasHash(baked.pages, baked.layout.frames);
      return hash;
    },
  };
}

/** SHA-256 over "WxH" + RGBA of every page, then the frame table as canonical JSON (sorted by id). */
export function atlasHash(pages: readonly CanvasLike[], frames: readonly PackedFrame[]): string {
  const s = new Sha256();
  for (const p of pages) {
    s.update(`${p.width}x${p.height};`);
    s.update(contextOf(p).getImageData(0, 0, p.width, p.height).data);
  }
  const sorted = [...frames].sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  s.update(
    JSON.stringify(
      sorted.map((f) => [f.id, f.page, f.x, f.y, f.w, f.h, f.tx, f.ty, f.sw, f.sh, f.pivot]),
    ),
  );
  return s.hex();
}

/** TexturePacker "JSON (Hash)" for one page, with pivots; multipacks list their siblings. */
export function texturePackerJson(
  atlas: Atlas,
  page: number,
  image: string,
  related: string[],
): unknown {
  const frames: Record<string, unknown> = {};
  for (const f of atlas.layout.frames) {
    if (f.page !== page) continue;
    frames[f.id] = {
      frame: { x: f.x, y: f.y, w: f.w, h: f.h },
      rotated: false,
      trimmed: f.tx !== 0 || f.ty !== 0 || f.w !== f.sw || f.h !== f.sh,
      spriteSourceSize: { x: f.tx, y: f.ty, w: f.w, h: f.h },
      sourceSize: { w: f.sw, h: f.sh },
      pivot: f.pivot,
    };
  }
  const p = atlas.pages[page];
  return {
    frames,
    meta: {
      app: 'gunmerger art (web/src/art)',
      version: '1.0',
      image,
      format: 'RGBA8888',
      size: { w: p?.width ?? 0, h: p?.height ?? 0 },
      scale: String(atlas.dpr),
      cellPx: atlas.px,
      related_multi_packs: related,
    },
  };
}
